import { BankOutlined, CloudSyncOutlined, DownOutlined, PlusOutlined, SwapOutlined, TagsOutlined, UploadOutlined } from '@ant-design/icons';
import type { ExpenseKind, PaymentStatus } from '@financiar/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Badge, Button, Card, Checkbox, DatePicker, Drawer, Dropdown, Empty, Input, Segmented, Select, Space, Statistic, Table, Typography } from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { getApiError } from '../../api/client';
import { CardRow, MobileCardList } from '../../components/MobileCardList';
import { DateText, formatMoney, MoneyText } from '../../lib/format';
import { useIsMobile } from '../../lib/responsive';
import { usePersistedState } from '../../lib/usePersistedState';
import { useFirmaCurenta } from '../firme/FirmaCurentaContext';
import {
  bulkSetExpenseCategory,
  deleteCheltuiala,
  fetchCheltuiala,
  fetchCheltuieli,
  fetchExpenseBankTransactions,
  fetchExpenseCategories,
  fetchSuppliers,
  markCheltuialaPaid,
  markCheltuialaUnpaid,
  type ExpenseDetail,
  type ExpenseListItem,
} from './api';
import { CategoriiModal } from './CategoriiModal';
import { CheltuialaDetailPanel } from './CheltuialaDetailPanel';
import { CheltuialaFormDrawer } from './CheltuialaFormDrawer';
import { ExpenseBankImportModal } from './ExpenseBankImportModal';
import { ReconciliereModal } from './ReconciliereModal';
import { SmartBillImportModal } from './SmartBillImportModal';
import { SpvSyncModal } from './SpvSyncModal';
import { ExpenseKindTag, ExpenseSourceTag, ExpenseStatusTag, KIND_LABELS } from './tags';

const { Title, Text } = Typography;
const PAGE_SIZE = 20;

type FilterKey = 'ALL' | PaymentStatus;
type SortBy = 'docDate' | 'supplier' | 'total' | 'paymentStatus';

const SEGMENT_OPTIONS: { label: string; value: FilterKey }[] = [
  { label: 'Toate', value: 'ALL' },
  { label: 'Neplătite', value: 'UNPAID' },
  { label: 'Parțiale', value: 'PARTIAL' },
  { label: 'Plătite', value: 'PAID' },
];

const DATE_PRESETS: { label: string; value: [Dayjs, Dayjs] }[] = [
  { label: 'Azi', value: [dayjs(), dayjs()] },
  { label: 'Ultimele 7 zile', value: [dayjs().subtract(6, 'day'), dayjs()] },
  { label: 'Ultimele 30 de zile', value: [dayjs().subtract(29, 'day'), dayjs()] },
  { label: 'Luna aceasta', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
  { label: 'Luna trecută', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
  { label: 'Anul acesta', value: [dayjs().startOf('year'), dayjs().endOf('year')] },
  { label: 'Anul trecut', value: [dayjs().subtract(1, 'year').startOf('year'), dayjs().subtract(1, 'year').endOf('year')] },
];

export function CheltuieliListPage() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const { firmaId } = useFirmaCurenta();
  const isMobile = useIsMobile();

  const [filter, setFilter] = usePersistedState<FilterKey>('cheltuieli.filter', 'ALL');
  const [page, setPage] = usePersistedState('cheltuieli.page', 1);
  const [search, setSearch] = usePersistedState('cheltuieli.search', '');
  const [rangeIso, setRangeIso] = usePersistedState<[string, string] | null>('cheltuieli.range', null);
  const range = useMemo<[Dayjs, Dayjs] | null>(
    () => (rangeIso ? [dayjs(rangeIso[0]), dayjs(rangeIso[1])] : null),
    [rangeIso],
  );
  const [kind, setKind] = usePersistedState<ExpenseKind | undefined>('cheltuieli.kind', undefined);
  const [categoryId, setCategoryId] = usePersistedState<string | undefined>('cheltuieli.categoryId', undefined);
  const [supplierId, setSupplierId] = usePersistedState<string | undefined>('cheltuieli.supplierId', undefined);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [sort, setSort] = usePersistedState<{ by: SortBy; order: 'asc' | 'desc' } | undefined>('cheltuieli.sort', undefined);
  // Deep link from the Contabilitate page („Documente lipsă” → Deschide):
  // `/cheltuieli?id=<expenseId>` opens that expense's panel, then the param
  // is dropped so a refresh or a later visit does not re-open it.
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(() => searchParams.get('id'));
  useEffect(() => {
    if (!searchParams.has('id')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  // Bulk selection (checkboxes) — independent of the split-view `selectedId`.
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [bulkCategoryId, setBulkCategoryId] = useState<string | undefined>(undefined);
  // Clear the selection when the user SWITCHES firm — not on the initial
  // null → id resolution, which would also wipe a deep-linked selection.
  const previousFirmaId = useRef(firmaId);
  useEffect(() => {
    if (previousFirmaId.current !== null && previousFirmaId.current !== firmaId) {
      setSelectedId(null);
      setSelectedRowKeys([]);
    }
    previousFirmaId.current = firmaId;
  }, [firmaId]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<ExpenseDetail | null>(null);
  const [bankImportOpen, setBankImportOpen] = useState(false);
  const [smartbillOpen, setSmartbillOpen] = useState(false);
  const [spvOpen, setSpvOpen] = useState(false);
  const [reconciliereOpen, setReconciliereOpen] = useState(false);
  const [categoriiOpen, setCategoriiOpen] = useState(false);

  const listParams = {
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
    kind,
    categoryId,
    supplierId,
    paymentStatus: filter === 'ALL' ? undefined : filter,
    dateFrom: range?.[0]?.format('YYYY-MM-DD'),
    dateTo: range?.[1]?.format('YYYY-MM-DD'),
    sortBy: sort?.by,
    sortOrder: sort?.order,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['expenses', firmaId, listParams],
    queryFn: () => fetchCheltuieli({ companyId: firmaId as string, ...listParams }),
    enabled: !!firmaId,
  });

  const { data: categories } = useQuery({
    queryKey: ['expense-categories', firmaId],
    queryFn: () => fetchExpenseCategories(firmaId as string),
    enabled: !!firmaId,
  });

  const { data: suppliersPage } = useQuery({
    queryKey: ['suppliers', firmaId, { search: supplierSearch }],
    queryFn: () => fetchSuppliers({ companyId: firmaId as string, search: supplierSearch || undefined }),
    enabled: !!firmaId,
  });

  // Toolbar badge: how many stored debit rows still await reconciliation.
  const { data: unmatchedPage } = useQuery({
    queryKey: ['expense-bank-tx', firmaId, { status: 'UNMATCHED', count: true }],
    queryFn: () => fetchExpenseBankTransactions({ companyId: firmaId as string, page: 1, pageSize: 1, status: 'UNMATCHED' }),
    enabled: !!firmaId,
  });
  const unmatchedCount = unmatchedPage?.total ?? 0;

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['expenses'] });
    queryClient.invalidateQueries({ queryKey: ['expense-bank-tx'] });
    queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
  }

  function reportError(error: unknown, fallback: string) {
    message.error(getApiError(error)?.message ?? fallback);
  }

  const deleteMutation = useMutation({
    mutationFn: deleteCheltuiala,
    onSuccess: () => {
      message.success('Cheltuiala a fost ștearsă.');
      setSelectedId(null);
      invalidate();
    },
    onError: (error) => reportError(error, 'Cheltuiala nu a putut fi ștearsă.'),
  });

  const markMutation = useMutation({
    mutationFn: ({ id, paid }: { id: string; paid: boolean }) => (paid ? markCheltuialaPaid(id) : markCheltuialaUnpaid(id)),
    onSuccess: () => invalidate(),
    onError: (error) => reportError(error, 'Statusul nu a putut fi schimbat.'),
  });

  const bulkCategoryMutation = useMutation({
    mutationFn: (categoryId: string | null) =>
      bulkSetExpenseCategory({ companyId: firmaId as string, expenseIds: selectedRowKeys, categoryId }),
    onSuccess: (result) => {
      message.success(`Categorie actualizată pentru ${result.updated} cheltuiel${result.updated === 1 ? 'ă' : 'i'}.`);
      setSelectedRowKeys([]);
      setBulkCategoryId(undefined);
      invalidate();
    },
    onError: (error) => reportError(error, 'Categoria nu a putut fi asociată.'),
  });

  async function openEdit(id: string) {
    try {
      const detail = await fetchCheltuiala(id);
      setEditingExpense(detail);
      setDrawerOpen(true);
    } catch (error) {
      reportError(error, 'Cheltuiala nu a putut fi încărcată.');
    }
  }

  function menuFor(row: ExpenseListItem): MenuProps['items'] {
    return [
      { key: 'view', label: 'Vezi' },
      { key: 'edit', label: 'Editează' },
      row.paymentStatus === 'PAID'
        ? { key: 'mark-unpaid', label: 'Marchează neplătită' }
        : { key: 'mark-paid', label: 'Marchează plătită' },
      { type: 'divider' as const },
      { key: 'delete', label: 'Șterge', danger: true },
    ];
  }

  function handleAction(row: ExpenseListItem, key: string) {
    switch (key) {
      case 'view':
        setSelectedId(row.id);
        return;
      case 'edit':
        void openEdit(row.id);
        return;
      case 'mark-paid':
        markMutation.mutate({ id: row.id, paid: true });
        return;
      case 'mark-unpaid':
        markMutation.mutate({ id: row.id, paid: false });
        return;
      case 'delete':
        modal.confirm({
          title: 'Ștergi cheltuiala?',
          content: 'Fișierele atașate se șterg odată cu ea. Alocările din extras trebuie șterse mai întâi.',
          okText: 'Șterge',
          cancelText: 'Renunță',
          okButtonProps: { danger: true },
          onOk: () => deleteMutation.mutateAsync(row.id),
        });
        return;
      default:
        return;
    }
  }

  const sortProps = (key: SortBy) => ({
    sorter: true as const,
    sortOrder: sort?.by === key ? ((sort.order === 'asc' ? 'ascend' : 'descend') as 'ascend' | 'descend') : null,
  });

  const columns: ColumnsType<ExpenseListItem> = [
    { title: 'Dată', key: 'docDate', width: 110, ...sortProps('docDate'), render: (_, row) => <DateText value={row.docDate} /> },
    {
      title: 'Document',
      key: 'document',
      width: 190,
      render: (_, row) => (
        <Space size={4} wrap>
          {row.docNumber ? <span className="num">{row.docNumber}</span> : <Text type="secondary">—</Text>}
          <ExpenseKindTag kind={row.kind} />
        </Space>
      ),
    },
    {
      title: 'Furnizor',
      key: 'supplier',
      ellipsis: true,
      ...sortProps('supplier'),
      render: (_, row) => row.supplier?.name ?? <Text type="secondary">{row.description ?? '—'}</Text>,
    },
    { title: 'Categorie', key: 'category', width: 170, ellipsis: true, render: (_, row) => row.category?.name ?? '—' },
    {
      title: 'Total',
      key: 'total',
      width: 160,
      align: 'right',
      ...sortProps('total'),
      render: (_, row) => (
        <div>
          <MoneyText value={row.totalAmount} currency={row.currency} />
          {row.currency !== 'RON' ? (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }} className="num">
                ≈ <MoneyText value={row.totalAmountRon} />
              </Text>
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 200,
      ...sortProps('paymentStatus'),
      render: (_, row) => (
        <Space size={4} wrap>
          <ExpenseStatusTag status={row.paymentStatus} />
          <ExpenseSourceTag source={row.source} />
        </Space>
      ),
    },
    {
      title: 'Acțiuni',
      key: 'actions',
      width: 110,
      render: (_, row) => (
        <span onClick={(event) => event.stopPropagation()} role="presentation">
          <Dropdown menu={{ items: menuFor(row), onClick: ({ key }) => handleAction(row, key) }} trigger={['click']}>
            <Button size="small">
              Acțiuni <DownOutlined />
            </Button>
          </Dropdown>
        </span>
      ),
    },
  ];

  const compactColumns: ColumnsType<ExpenseListItem> = [
    {
      title: 'Document',
      key: 'document',
      width: 150,
      render: (_, row) => (
        <div>
          <Space size={4}>{row.docNumber ? <span className="num">{row.docNumber}</span> : <ExpenseKindTag kind={row.kind} />}</Space>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <DateText value={row.docDate} />
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Furnizor',
      key: 'supplier',
      ellipsis: true,
      render: (_, row) => row.supplier?.name ?? (row.description || KIND_LABELS[row.kind]),
    },
    {
      title: 'Total',
      key: 'total',
      width: 130,
      align: 'right',
      render: (_, row) => <MoneyText value={row.totalAmount} currency={row.currency} />,
    },
    { title: 'Status', key: 'status', width: 120, render: (_, row) => <ExpenseStatusTag status={row.paymentStatus} /> },
  ];

  const splitView = selectedId !== null;
  const totals = data?.totals;

  function openCreate() {
    setEditingExpense(null);
    setDrawerOpen(true);
  }

  // Pe mobil, acțiunile secundare din antet (5 butoane) se strâng într-un
  // singur dropdown „Acțiuni", ca să nu ocupe un teanc de butoane pe verticală.
  const secondaryActionItems: MenuProps['items'] = [
    { key: 'categorii', icon: <TagsOutlined />, label: 'Categorii', disabled: !firmaId },
    { key: 'tranzactii', icon: <SwapOutlined />, label: unmatchedCount > 0 ? `Tranzacții (${unmatchedCount})` : 'Tranzacții', disabled: !firmaId },
    { key: 'spv', icon: <CloudSyncOutlined />, label: 'Sincronizare SPV', disabled: !firmaId },
    { key: 'smartbill', icon: <UploadOutlined />, label: 'Import SmartBill', disabled: !firmaId },
    { key: 'extras', icon: <BankOutlined />, label: 'Importă extras' },
  ];
  function handleSecondaryAction(key: string) {
    if (key === 'categorii') setCategoriiOpen(true);
    else if (key === 'tranzactii') setReconciliereOpen(true);
    else if (key === 'spv') setSpvOpen(true);
    else if (key === 'smartbill') setSmartbillOpen(true);
    else if (key === 'extras') setBankImportOpen(true);
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 16,
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          Cheltuieli
        </Title>
        {isMobile ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <Dropdown menu={{ items: secondaryActionItems, onClick: ({ key }) => handleSecondaryAction(key) }} trigger={['click']}>
              <Button block style={{ flex: 1 }}>
                Acțiuni <DownOutlined />
              </Button>
            </Dropdown>
            <Button type="primary" icon={<PlusOutlined />} block style={{ flex: 1 }} disabled={!firmaId} onClick={openCreate}>
              Adaugă
            </Button>
          </div>
        ) : (
          <Space wrap>
            <Button icon={<TagsOutlined />} onClick={() => setCategoriiOpen(true)} disabled={!firmaId}>
              Categorii
            </Button>
            <Badge count={unmatchedCount} size="small" offset={[-6, 4]}>
              <Button icon={<SwapOutlined />} onClick={() => setReconciliereOpen(true)} disabled={!firmaId}>
                Tranzacții
              </Button>
            </Badge>
            <Button icon={<CloudSyncOutlined />} onClick={() => setSpvOpen(true)} disabled={!firmaId}>
              Sincronizare SPV
            </Button>
            <Button icon={<UploadOutlined />} onClick={() => setSmartbillOpen(true)} disabled={!firmaId}>
              Import SmartBill
            </Button>
            <Button icon={<BankOutlined />} onClick={() => setBankImportOpen(true)}>
              Importă extras
            </Button>
            <Button type="primary" icon={<PlusOutlined />} disabled={!firmaId} onClick={openCreate}>
              Adaugă cheltuială
            </Button>
          </Space>
        )}
      </div>

      {!firmaId ? (
        <Empty description="Selectează sau adaugă mai întâi o firmă." />
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 8 : 16, marginBottom: 16 }}>
            <Card size="small" style={{ flex: isMobile ? '1 1 100px' : '0 0 auto', minWidth: isMobile ? 0 : 200 }}>
              <Statistic
                title="Total cheltuieli (filtrat)"
                value={totals ? formatMoney(totals.totalRon) : '—'}
                valueStyle={isMobile ? { fontSize: 18 } : undefined}
              />
            </Card>
            <Card size="small" style={{ flex: isMobile ? '1 1 100px' : '0 0 auto', minWidth: isMobile ? 0 : 180 }}>
              <Statistic
                title="Plătit"
                value={totals ? formatMoney(totals.paidRon) : '—'}
                valueStyle={{ color: '#0F6E4F', ...(isMobile ? { fontSize: 18 } : {}) }}
              />
            </Card>
            <Card size="small" style={{ flex: isMobile ? '1 1 100px' : '0 0 auto', minWidth: isMobile ? 0 : 180 }}>
              <Statistic
                title="Neplătit"
                value={totals ? formatMoney(totals.unpaidRon) : '—'}
                valueStyle={{
                  ...(totals && totals.unpaidRon !== '0.00' ? { color: '#cf1322' } : {}),
                  ...(isMobile ? { fontSize: 18 } : {}),
                }}
              />
            </Card>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              flexWrap: 'wrap',
              alignItems: isMobile ? 'stretch' : 'center',
              gap: 12,
              marginBottom: 16,
            }}
          >
            <Segmented<FilterKey>
              block={isMobile}
              options={SEGMENT_OPTIONS}
              value={filter}
              onChange={(value) => {
                setFilter(value);
                setPage(1);
              }}
            />
            <DatePicker.RangePicker
              value={range}
              format="DD.MM.YYYY"
              placeholder={['De la', 'Până la']}
              presets={DATE_PRESETS}
              style={isMobile ? { width: '100%' } : undefined}
              onChange={(values) => {
                setRangeIso(
                  values && values[0] && values[1]
                    ? [values[0].format('YYYY-MM-DD'), values[1].format('YYYY-MM-DD')]
                    : null,
                );
                setPage(1);
              }}
            />
            <Select
              allowClear
              style={{ width: isMobile ? '100%' : 130 }}
              placeholder="Toate tipurile"
              value={kind}
              onChange={(value: ExpenseKind | undefined) => {
                setKind(value);
                setPage(1);
              }}
              options={(Object.keys(KIND_LABELS) as ExpenseKind[]).map((value) => ({ value, label: KIND_LABELS[value] }))}
            />
            <Select
              allowClear
              style={{ width: isMobile ? '100%' : 180 }}
              placeholder="Toate categoriile"
              value={categoryId}
              onChange={(value: string | undefined) => {
                setCategoryId(value);
                setPage(1);
              }}
              options={(categories ?? []).map((category) => ({ value: category.id, label: category.name }))}
            />
            <Select
              allowClear
              showSearch
              filterOption={false}
              style={{ width: isMobile ? '100%' : 220 }}
              placeholder="Toți furnizorii"
              value={supplierId}
              onSearch={setSupplierSearch}
              onChange={(value: string | undefined) => {
                setSupplierId(value);
                setPage(1);
              }}
              options={(suppliersPage?.items ?? []).map((supplier) => ({ value: supplier.id, label: supplier.name }))}
            />
            <Input.Search
              placeholder="Caută după număr, furnizor, descriere"
              allowClear
              defaultValue={search}
              style={{ width: isMobile ? '100%' : 260 }}
              onSearch={(value) => {
                setSearch(value);
                setPage(1);
              }}
            />
          </div>

          {selectedRowKeys.length > 0 ? (
            <Space wrap style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f7f4', borderRadius: 6 }}>
              <Text strong>{selectedRowKeys.length} selectate</Text>
              <Select
                placeholder="Alege categoria"
                style={{ width: isMobile ? '100%' : 220 }}
                value={bulkCategoryId}
                onChange={setBulkCategoryId}
                options={(categories ?? []).map((category) => ({ value: category.id, label: category.name }))}
              />
              <Button
                type="primary"
                disabled={!bulkCategoryId}
                loading={bulkCategoryMutation.isPending}
                onClick={() => bulkCategoryMutation.mutate(bulkCategoryId ?? null)}
              >
                Asociază categoria
              </Button>
              <Button loading={bulkCategoryMutation.isPending} onClick={() => bulkCategoryMutation.mutate(null)}>
                Elimină categoria
              </Button>
              <Button type="text" onClick={() => setSelectedRowKeys([])}>
                Deselectează
              </Button>
            </Space>
          ) : null}

          {isMobile ? (
            <>
              <MobileCardList<ExpenseListItem>
                dataSource={data?.items ?? []}
                rowKey={(row) => row.id}
                loading={isLoading}
                emptyText="Nicio cheltuială."
                onCardClick={(row) => setSelectedId(row.id)}
                pagination={{ current: page, pageSize: PAGE_SIZE, total: data?.total ?? 0, onChange: setPage }}
                renderCard={(row) => (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', gap: 8, minWidth: 0, alignItems: 'flex-start' }}>
                        <span onClick={(event) => event.stopPropagation()} role="presentation">
                          <Checkbox
                            checked={selectedRowKeys.includes(row.id)}
                            onChange={(event) =>
                              setSelectedRowKeys((keys) =>
                                event.target.checked ? [...keys, row.id] : keys.filter((k) => k !== row.id),
                              )
                            }
                          />
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <Space size={4} wrap>
                            {row.docNumber ? <span className="num">{row.docNumber}</span> : <Text type="secondary">—</Text>}
                            <ExpenseKindTag kind={row.kind} />
                          </Space>
                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              <DateText value={row.docDate} />
                            </Text>
                          </div>
                        </div>
                      </div>
                      <span onClick={(event) => event.stopPropagation()} role="presentation">
                        <Dropdown menu={{ items: menuFor(row), onClick: ({ key }) => handleAction(row, key) }} trigger={['click']}>
                          <Button size="small">
                            Acțiuni <DownOutlined />
                          </Button>
                        </Dropdown>
                      </span>
                    </div>
                    <div style={{ marginTop: 6, fontWeight: 500 }}>
                      {row.supplier?.name ?? (row.description || KIND_LABELS[row.kind])}
                    </div>
                    <CardRow label="Categorie">{row.category?.name ?? '—'}</CardRow>
                    <CardRow label="Total">
                      <MoneyText value={row.totalAmount} currency={row.currency} />
                      {row.currency !== 'RON' ? (
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }} className="num">
                            ≈ <MoneyText value={row.totalAmountRon} />
                          </Text>
                        </div>
                      ) : null}
                    </CardRow>
                    <div style={{ marginTop: 6 }}>
                      <Space size={4} wrap>
                        <ExpenseStatusTag status={row.paymentStatus} />
                        <ExpenseSourceTag source={row.source} />
                      </Space>
                    </div>
                  </>
                )}
              />
              <Drawer
                open={splitView}
                placement="right"
                width="100%"
                closable={false}
                styles={{ body: { padding: 12 } }}
                onClose={() => setSelectedId(null)}
              >
                {selectedId ? (
                  <CheltuialaDetailPanel
                    id={selectedId}
                    onClose={() => setSelectedId(null)}
                    onEdit={(expense) => {
                      setEditingExpense(expense);
                      setDrawerOpen(true);
                    }}
                  />
                ) : null}
              </Drawer>
            </>
          ) : (
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <div style={{ flex: splitView ? '0 0 44%' : '1 1 100%', minWidth: 0 }}>
                <Table<ExpenseListItem>
                  rowKey="id"
                  columns={splitView ? compactColumns : columns}
                  dataSource={data?.items ?? []}
                  loading={isLoading}
                  rowSelection={{
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys as string[]),
                    preserveSelectedRowKeys: true,
                  }}
                  rowClassName={(row) => (row.id === selectedId ? 'ant-table-row-selected' : '')}
                  onChange={(_pagination, _filters, sorter) => {
                    const s = Array.isArray(sorter) ? sorter[0] : sorter;
                    const next =
                      s?.order && s.columnKey
                        ? { by: s.columnKey as SortBy, order: (s.order === 'ascend' ? 'asc' : 'desc') as 'asc' | 'desc' }
                        : undefined;
                    const changed = (next?.by ?? null) !== (sort?.by ?? null) || (next?.order ?? null) !== (sort?.order ?? null);
                    if (changed) {
                      setSort(next);
                      setPage(1);
                    }
                  }}
                  onRow={(row) => ({
                    style: { cursor: 'pointer' },
                    onClick: () => setSelectedId(row.id),
                  })}
                  scroll={{ x: 'max-content', y: 'calc(100vh - 430px)' }}
                  sticky
                  pagination={{
                    current: page,
                    pageSize: PAGE_SIZE,
                    total: data?.total ?? 0,
                    onChange: setPage,
                    showSizeChanger: false,
                  }}
                />
              </div>
              {splitView && selectedId ? (
                <div style={{ flex: '1 1 56%', minWidth: 0 }}>
                  <CheltuialaDetailPanel
                    id={selectedId}
                    onClose={() => setSelectedId(null)}
                    onEdit={(expense) => {
                      setEditingExpense(expense);
                      setDrawerOpen(true);
                    }}
                  />
                </div>
              ) : null}
            </div>
          )}
        </>
      )}

      {firmaId ? (
        <>
          <CheltuialaFormDrawer
            open={drawerOpen}
            companyId={firmaId}
            expense={editingExpense}
            onClose={() => {
              setDrawerOpen(false);
              setEditingExpense(null);
            }}
            onSaved={() => {
              invalidate();
              if (editingExpense) {
                queryClient.invalidateQueries({ queryKey: ['expenses', 'detail', editingExpense.id] });
              }
            }}
          />
          <SmartBillImportModal open={smartbillOpen} firmaId={firmaId} onClose={() => setSmartbillOpen(false)} onDone={invalidate} />
          <SpvSyncModal open={spvOpen} firmaId={firmaId} onClose={() => setSpvOpen(false)} onDone={invalidate} />
          <ReconciliereModal open={reconciliereOpen} firmaId={firmaId} onClose={() => setReconciliereOpen(false)} onChanged={invalidate} />
          <CategoriiModal open={categoriiOpen} firmaId={firmaId} onClose={() => setCategoriiOpen(false)} />
        </>
      ) : null}

      <ExpenseBankImportModal
        open={bankImportOpen}
        onClose={() => setBankImportOpen(false)}
        onDone={() => {
          // The statement's own "Cod unic:" resolves the firm — not
          // necessarily the selected one — and a new bank account may exist.
          invalidate();
          queryClient.invalidateQueries({ queryKey: ['firms'] });
        }}
      />
    </div>
  );
}
